import React, { useState, useEffect } from "react";
import { Upload, Select, message, Card } from "antd";
import {
  UploadOutlined,
  FilePdfOutlined,
  IdcardOutlined,
  BankOutlined,
} from "@ant-design/icons";
import { motion } from "framer-motion";
import type { UploadFile, UploadProps } from "antd/es/upload/interface";
import '../../styles/antd-overrides.css';

const { Option } = Select;

interface ProfessionalDocumentUploadProps {
  onDocumentsChange: (documents: {
    kbisFile: File | null;
    identityRecto: File | null;
    identityVerso: File | null;
    identityType: "cin" | "passeport";
    ribFile: File | null;
  }) => void;
  initial?: {
    kbisUrl?: string;
    rectoUrl?: string;
    versoUrl?: string;
    identityType?: "cin" | "passeport";
    ribUrl?: string;
  };
}

const getFileNameFromUrl = (url?: string, fallback = "file") => {
  if (!url) return fallback;
  return url.split("/").pop() || fallback;
};

const cardMotion = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  whileHover: { scale: 1.01 },
  transition: { duration: 0.25 },
};


const ProfessionalDocumentUpload: React.FC<
  ProfessionalDocumentUploadProps
> = ({ onDocumentsChange, initial }) => {
  const [kbisFile, setKbisFile] = useState<File | null>(null);
  const [identityRecto, setIdentityRecto] = useState<File | null>(null);
  const [identityVerso, setIdentityVerso] = useState<File | null>(null);
  const [ribFile, setRibFile] = useState<File | null>(null);

  const [identityType, setIdentityType] = useState<
    "cin" | "passeport"
  >(initial?.identityType || "cin");

  const [kbisFileList, setKbisFileList] = useState<UploadFile[]>(
    initial?.kbisUrl
      ? [
        {
          uid: "-kbis",
          name: getFileNameFromUrl(initial.kbisUrl, "kbis.pdf"),
          status: "done",
          url: initial.kbisUrl,
        },
      ]
      : []
  );

  const [rectoFileList, setRectoFileList] = useState<UploadFile[]>(
    initial?.rectoUrl
      ? [
        {
          uid: "-recto",
          name: getFileNameFromUrl(initial.rectoUrl, "recto.jpg"),
          status: "done",
          url: initial.rectoUrl,
        },
      ]
      : []
  );

  const [versoFileList, setVersoFileList] = useState<UploadFile[]>(
    initial?.versoUrl
      ? [
        {
          uid: "-verso",
          name: getFileNameFromUrl(initial.versoUrl, "verso.jpg"),
          status: "done",
          url: initial.versoUrl,
        },
      ]
      : []
  );

  const [ribFileList, setRibFileList] = useState<UploadFile[]>(
    initial?.ribUrl
      ? [
        {
          uid: "-rib",
          name: getFileNameFromUrl(initial.ribUrl, "rib.pdf"),
          status: "done",
          url: initial.ribUrl,
        },
      ]
      : []
  );

  const notifyParent = (
    kbis: File | null,
    recto: File | null,
    verso: File | null,
    type: "cin" | "passeport",
    rib: File | null
  ) => {
    onDocumentsChange({
      kbisFile: kbis,
      identityRecto: recto,
      identityVerso: verso,
      identityType: type,
      ribFile: rib,
    });
  };

  const simpleImageCheck = (file: File, maxMb = 5) => {
    const okType = ["image/jpeg", "image/png", "image/webp"].includes(
      file.type
    );
    if (!okType) {
      message.error("Format image invalide");
      return false;
    }
    if (file.size / 1024 / 1024 > maxMb) {
      message.error(`Taille max ${maxMb}Mo`);
      return false;
    }
    return true;
  };

  const simplePdfCheck = (file: File, maxMb = 10) => {
    if (file.type !== "application/pdf") {
      message.error("Format PDF requis");
      return false;
    }
    if (file.size / 1024 / 1024 > maxMb) {
      message.error(`Taille max ${maxMb}Mo`);
      return false;
    }
    return true;
  };

  return (
    <Card title="Documents à fournir pour le compte professionnel"
      headStyle={{
        background: "linear-gradient(135deg, #4BB0C0 0%, #2A919D 100%)",
        color: "#fff",
      }}>
      <div style={{ display: "grid", gap: 16 }}>
        <motion.div {...cardMotion}>
          <DocumentCard
            icon={<FilePdfOutlined />}
            title="Extrait Kbis"
            description="Justifie l’immatriculation de l’entreprise"
            provided={!!kbisFileList.length}
          >
            <Upload
              beforeUpload={(file) => {
                if (!simplePdfCheck(file)) return Upload.LIST_IGNORE;
                setKbisFile(file);
                setKbisFileList([
                  {
                    uid: file.uid,
                    name: file.name,
                    status: "done",
                  },
                ]);
                notifyParent(file, identityRecto, identityVerso, identityType, ribFile);
                return false;
              }}
              fileList={kbisFileList}
              onRemove={() => {
                setKbisFile(null);
                setKbisFileList([]);
              }}
              maxCount={1}
            >
              <ActionButton />
            </Upload>
          </DocumentCard>
        </motion.div>

        <motion.div {...cardMotion}>
          <DocumentCard
            icon={<IdcardOutlined />}
            title="Type de pièce d’identité"
            description="CNI ou Passeport"
            provided
          >
            <Select
              value={identityType}
              onChange={(v) => setIdentityType(v)}
              style={{ width: 220 }}
            >
              <Option value="cin">CNI</Option>
              <Option value="passeport">Passeport</Option>
            </Select>
          </DocumentCard>
        </motion.div>

        <motion.div {...cardMotion}>
          <DocumentCard
            icon={<IdcardOutlined />}
            title={
              identityType === "passeport"
                ? "Passeport"
                : "Pièce d’identité – Recto"
            }
            description="Document officiel"
            provided={!!rectoFileList.length}
          >
            <Upload
              beforeUpload={(file) => {
                if (!simpleImageCheck(file)) return Upload.LIST_IGNORE;
                setIdentityRecto(file);
                setRectoFileList([
                  { uid: file.uid, name: file.name, status: "done" },
                ]);
                notifyParent(kbisFile, file, identityVerso, identityType, ribFile);
                return false;
              }}
              fileList={rectoFileList}
              onRemove={() => setRectoFileList([])}
              maxCount={1}
              listType="picture"
            >
              <ActionButton />
            </Upload>
          </DocumentCard>
        </motion.div>

        {/* ================= VERSO ================= */}
        {identityType === "cin" && (
          <motion.div {...cardMotion}>
            <DocumentCard
              icon={<IdcardOutlined />}
              title="Pièce d’identité – Verso"
              description="CNI uniquement"
              provided={!!versoFileList.length}
            >
              <Upload
                beforeUpload={(file) => {
                  if (!simpleImageCheck(file)) return Upload.LIST_IGNORE;
                  setIdentityVerso(file);
                  setVersoFileList([
                    { uid: file.uid, name: file.name, status: "done" },
                  ]);
                  notifyParent(kbisFile, identityRecto, file, identityType, ribFile);
                  return false;
                }}
                fileList={versoFileList}
                onRemove={() => setVersoFileList([])}
                maxCount={1}
                listType="picture"
              >
                <ActionButton />
              </Upload>
            </DocumentCard>
          </motion.div>
        )}

        {/* ================= RIB ================= */}
        <motion.div {...cardMotion}>
          <DocumentCard
            icon={<BankOutlined />}
            title="RIB"
            description="Document bancaire"
            provided={!!ribFileList.length}
          >
            <Upload
              beforeUpload={(file) => {
                setRibFile(file);
                setRibFileList([
                  { uid: file.uid, name: file.name, status: "done" },
                ]);
                notifyParent(kbisFile, identityRecto, identityVerso, identityType, file);
                return false;
              }}
              fileList={ribFileList}
              onRemove={() => setRibFileList([])}
              maxCount={1}
            >
              <ActionButton />
            </Upload>
          </DocumentCard>
        </motion.div>
      </div>
    </Card>
  );
};

const DocumentCard: React.FC<{
  icon: React.ReactNode;
  title: string;
  description: string;
  provided: boolean;
  children: React.ReactNode;
}> = ({ icon, title, description, provided, children }) => (
  <div
    style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: 16,
      border: "1px solid #e5e7eb",
      borderRadius: 10,
      background: "#fff",
    }}
  >
    <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
      <div style={{ fontSize: 22, color: "#2A919D" }}>{icon}</div>
      <div>
        <div style={{ fontWeight: 600 }}>{title}</div>
        <div style={{ fontSize: 12, color: "#6b7280" }}>{description}</div>
      </div>
    </div>

    <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
      {/* {provided && <CheckCircleFilled style={{ color: "#22c55e" }} />} */}
      {children}
    </div>
  </div>
);

const ActionButton = () => (
  <motion.div whileHover={{ scale: 1.05 }}>
    <UploadOutlined style={{ fontSize: 18, color: "#eb8635" }} />
  </motion.div>
);

export default ProfessionalDocumentUpload;
